import React, { useEffect, useState } from 'react';
import { Book, TestBank } from '../../../../model/response';
import { useMasterGradesSelector, useMasterSubjectsSelector } from '../../../../redux/selector';
import { Button, Checkbox, Drawer, DrawerProps, Input, InputNumber, message, Select } from 'antd';
import processError from '../../../../utils/processError';
import { mapToSelectItems } from '../../../../utils/utils';
import { BookRequest } from '../../../../model/request';
import { createBook, editBook } from '../../../../service/bookService';
import { findAllTestBank } from '../../../../service/testBankService';
import CkEditorWord4 from '../../../common/CkEditorWord4/CkEditorWord4';
import FileUpload from '../../../common/FileUpload';
import { uuidv4 } from '../../../../common/commonFunc';

interface BookFormDrawerProps extends Omit<DrawerProps, 'footer'> {
  mode: 'create' | 'edit';
  defaultBook: Book | null;
  onBeforeSuccess: () => Promise<void>;
}

const config = {
  toolbar: [
    { name: 'document', items: ['Source'] },
    { name: 'styles', items: ['Format', 'Font', 'FontSize'] },
    {
      name: 'basicstyles',
      items: [
        'Bold',
        'Italic',
        'Underline',
        'Strike',
        'Subscript',
        'Superscript',
        '-',
        'CopyFormatting',
        'RemoveFormat',
      ],
    },
    { name: 'colors', items: ['TextColor', 'BGColor'] },
    {
      name: 'paragraph',
      items: [
        'NumberedList',
        'BulletedList',
        '-',
        'Outdent',
        'Indent',
        '-',
        'Blockquote',
        '-',
        'JustifyLeft',
        'JustifyCenter',
        'JustifyRight',
        'JustifyBlock',
        '-',
        'BidiLtr',
        'BidiRtl',
      ],
    },
    { name: 'links', items: ['Link', 'Unlink', 'Anchor'] },
    {
      name: 'insert',
      items: ['SpecialChar', 'PageBreak', 'fillBlank', 'selectForm'],
    },
  ],
  height: 200,
  extraPlugins: 'niw_btn',
  allowedContent: true,
  font_defaultLabel: 'Arial',
  fontSize_defaultLabel: 16,
  contentsCss: ["body {font-size: 18px; font-family: 'Arial';word-wrap:break-word;}"],
  actionFocus: false,
};

function BookFormDrawer({ mode, defaultBook, open, onBeforeSuccess, onClose, ...restProps }: BookFormDrawerProps) {
  const subjects = useMasterSubjectsSelector();
  const [id, setId] = useState('');
  const [name, setName] = useState<string>('');
  const [publishCode, setPublishCode] = useState('');
  const [code, setCode] = useState<string>('');
  const [subjectId, setSubjectId] = useState<string>('');
  const [grades, setGrades] = useState<string[]>([]);
  const [preface, setPreface] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [audio, setAudio] = useState('');
  const [publishYear, setPublishYear] = useState('');
  const [testBankId, setTestBankId] = useState('');
  const gradeMasterList = useMasterGradesSelector();
  const [testBanks, setTestBanks] = useState<TestBank[]>([]);
  const [webViewActive, setWebViewActive] = useState(true);
  const [webViewOrder, setWebViewOrder] = useState<number>(1);

  useEffect(() => {
    findAllTestBank().then((response) => setTestBanks(response));
  }, []);

  const clearForm = () => {
    setId('');
    setName('');
    setCode('');
    setPublishCode('');
    setPreface('');
    setSubjectId('');
    setTestBankId('');
    setPublishYear('');
    setThumbnail('');
    setAudio('');
    setGrades([]);
  };

  async function handleSaveBook() {
    const booRequest: BookRequest = {
      name,
      publishCode,
      code,
      preface,
      publishYear,
      audio,
      thumbnail,
      testBank: {
        id: testBankId,
      },
      subject: {
        id: subjectId,
      },
      grades: grades.map((id) => ({ id })),
      webViewActive: webViewActive,
      webViewOrder: webViewOrder,
    };
    try {
      mode === 'create' ? await createBook(booRequest) : await editBook(id, booRequest);
      message.success(mode === 'create' ? 'Thêm mới sách thành công' : 'Sửa sách thành công');
      clearForm();
      await onBeforeSuccess();
    } catch (error) {
      processError(error);
    }
  }

  useEffect(() => {
    if (open && defaultBook) {
      setId(defaultBook.id);
      setName(defaultBook.name);
      setCode(defaultBook.code);
      setPublishCode(defaultBook.publishCode || '');
      setPreface(defaultBook.preface || '');
      setAudio(defaultBook.audio || '');
      setThumbnail(defaultBook.thumbnail || '');
      setPublishYear(defaultBook.publishYear || '');
      setSubjectId(defaultBook.subject?.id || '');
      setWebViewActive(defaultBook?.webViewActive);
      setWebViewOrder(defaultBook?.webViewOrder);
      setTestBankId(defaultBook.testBank?.id || '');
      setGrades(defaultBook.grades?.map((grade) => grade.id) || []);
    } else {
      clearForm();
    }
  }, [defaultBook, defaultBook?.id, open]);

  useEffect(() => {
    if (open && mode === 'create') {
      setId(uuidv4());
    }
  }, [mode, open]);

  return (
    <Drawer
      title={mode === 'edit' ? 'Sửa sách' : 'Thêm sách'}
      placement="right"
      width={400}
      closable={false}
      open={open}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button onClick={onClose} className={'mr-2'}>
            Hủy
          </Button>
          <Button type="primary" onClick={handleSaveBook}>
            Lưu
          </Button>
        </div>
      }
      onClose={onClose}
      {...restProps}
    >
      <div className={'flex flex-col gap-y-2'}>
        <div>
          <label>
            Tên sách <span className={'text-FF1D30'}>*</span>
          </label>
          <Input placeholder="Nhập tên sách..." value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label>Giới thiệu sách</label>
          <CkEditorWord4
            config={config}
            value={preface}
            onChangeEditor={(content) => {
              setPreface(content);
            }}
            keyId={id}
          />
        </div>
        <div>
          <label>Audio</label>
          <FileUpload
            url={thumbnail}
            setUrl={setThumbnail}
            accept={'.jpg,.png,.jpeg'}
            label={'Tải thumbnail'}
            placeholder={'Link thumbnail...'}
          />
        </div>
        <div>
          <label>Audio</label>
          <FileUpload
            url={audio}
            setUrl={setAudio}
            accept={'audio/*'}
            label={'Tải audio'}
            placeholder={'Link audio...'}
          />
        </div>
        <div>
          <label>Mã xuất bản</label>
          <Input
            placeholder="Nhập mã xuất bản..."
            value={publishCode}
            onChange={(e) => setPublishCode(e.target.value)}
            required
          />
        </div>
        <div>
          <label>Năm phát hành</label>
          <Input
            placeholder="Nhập năm phát hành..."
            value={publishYear}
            onChange={(e) => setPublishYear(e.target.value)}
            required
          />
        </div>
        <div>
          <label>
            Mã sách <span className={'text-FF1D30'}>*</span>
          </label>
          <Input
            placeholder="Nhập ID sách..."
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            required
          />
        </div>
        <div>
          <label>
            Môn học <span className={'text-FF1D30'}>*</span>
          </label>
          <Select
            placeholder="Chọn môn học"
            value={subjectId}
            onChange={setSubjectId}
            style={{ width: '100%' }}
            options={mapToSelectItems(subjects)}
          />
        </div>
        <div>
          <label>Ngân hàng</label>
          <Select
            placeholder="Chọn ngân hàng"
            value={testBankId}
            onChange={setTestBankId}
            style={{ width: '100%' }}
            options={mapToSelectItems(testBanks)}
          />
        </div>
        <div>
          <label>Lớp học</label> <br />
          <Checkbox.Group options={mapToSelectItems(gradeMasterList)} value={grades} onChange={setGrades} />
        </div>
        <div>
          <label>Hiển thị trên web</label> <br />
          <Checkbox checked={webViewActive} onChange={(e) => setWebViewActive(e.target.checked)} />
        </div>
        <div>
          <label>
            Độ ưu tiên hiển thị <span className={'text-FF1D30'}>*</span>
          </label>{' '}
          <br />
          <InputNumber
            placeholder="Nhập độ ưu tiên hiển thị..."
            value={webViewOrder}
            onChange={(e) => {
              setWebViewOrder(e || 1);
            }}
            required
          />
        </div>
      </div>
    </Drawer>
  );
}

export default BookFormDrawer;
